// src/features/executions/api/useExecutions.ts
// Executions CRUD + Steps + Build/TestPlan Results + Test Runs
// All endpoints verified against openapi.json (execution-service, testrun-service)
// All paths via API_CONFIG.ENDPOINTS

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import type {
  Execution,

  CreateExecutionRequest,
  UpdateExecutionRequest,
  ExecutionStep,
  ExecutionStepsResponse,
  CreateExecutionStepRequest,
  BuildResults,
  TestPlanResults,
  TestRun,
} from '@/types/execution.types'

// ============================================================================
// Query Keys
// ============================================================================

export const executionKeys = {
  all: ['executions'] as const,
  lists: () => [...executionKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...executionKeys.lists(), filters] as const,
  details: () => [...executionKeys.all, 'detail'] as const,
  detail: (id: number) => [...executionKeys.details(), id] as const,
  steps: (id: number) => [...executionKeys.all, 'steps', id] as const,
  buildResults: (buildId: number) => ['builds', buildId, 'results'] as const,
  testplanResults: (testplanId: number) =>
    ['testplans', testplanId, 'results'] as const,
}

// ============================================================================
// Executions — CRUD
// OpenAPI: GET /api/executions, GET/PUT/DELETE /api/executions/{id}
//          POST /api/executions
// ============================================================================

// List executions with optional filters
export const useExecutions = (params?: {
  build_id?: number
  testplan_id?: number
  tcversion_id?: number
  platform_id?: number
  status?: string
  limit?: number
  offset?: number
}) => {
  return useQuery({
    queryKey: executionKeys.list(params),
    queryFn: async () => {
      const qp = new URLSearchParams()
      if (params?.build_id)
        qp.append('build_id', params.build_id.toString())
      if (params?.testplan_id)
        qp.append('testplan_id', params.testplan_id.toString())
      if (params?.tcversion_id)
        qp.append('tcversion_id', params.tcversion_id.toString())
      if (params?.platform_id)
        qp.append('platform_id', params.platform_id.toString())
      if (params?.status) qp.append('status', params.status)
      if (params?.limit) qp.append('limit', params.limit.toString())
      if (params?.offset) qp.append('offset', params.offset.toString())

      const url = qp.toString()
        ? `${API_CONFIG.ENDPOINTS.EXECUTIONS.LIST}?${qp.toString()}`
        : API_CONFIG.ENDPOINTS.EXECUTIONS.LIST
      const { data } = await apiClient.get<Execution[]>(url)
      return data
    },
  })
}

// Get single execution by ID
export const useExecution = (id: number) => {
  return useQuery({
    queryKey: executionKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<Execution>(
        API_CONFIG.ENDPOINTS.EXECUTIONS.GET(id.toString())
      )
      return data
    },
    enabled: !!id,
  })
}

// Create execution
export const useCreateExecution = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (executionData: CreateExecutionRequest) => {
      const { data } = await apiClient.post<Execution>(
        API_CONFIG.ENDPOINTS.EXECUTIONS.CREATE,
        executionData
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() })
    },
  })
}

// Update execution
export const useUpdateExecution = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: UpdateExecutionRequest
    }) => {
      const response = await apiClient.put<Execution>(
        API_CONFIG.ENDPOINTS.EXECUTIONS.UPDATE(id.toString()),
        data
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: executionKeys.detail(data.id) })
    },
  })
}

// Delete execution
export const useDeleteExecution = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(
        API_CONFIG.ENDPOINTS.EXECUTIONS.DELETE(id.toString())
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() })
    },
  })
}

// ============================================================================
// Execution Steps
// OpenAPI: GET /api/executions/{id}/steps, POST /api/executions/{id}/steps
// ============================================================================

export const useExecutionSteps = (executionId: number) => {
  return useQuery({
    queryKey: executionKeys.steps(executionId),
    queryFn: async () => {
      const { data } = await apiClient.get<ExecutionStepsResponse>(
        API_CONFIG.ENDPOINTS.EXECUTIONS.STEPS(executionId.toString())
      )
      return data.steps
    },
    enabled: !!executionId,
  })
}

export const useCreateExecutionStep = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      executionId,
      data,
    }: {
      executionId: number
      data: CreateExecutionStepRequest
    }) => {
      const response = await apiClient.post<ExecutionStep>(
        API_CONFIG.ENDPOINTS.EXECUTIONS.CREATE_STEP(executionId.toString()),
        data
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: executionKeys.steps(variables.executionId),
      })
    },
  })
}

// ============================================================================
// Build and TestPlan Results
// OpenAPI: GET /api/builds/{build_id}/results
//          GET /api/testplans/{testplan_id}/results
// ============================================================================

export const useBuildResults = (buildId: number) => {
  return useQuery({
    queryKey: executionKeys.buildResults(buildId),
    queryFn: async () => {
      const { data } = await apiClient.get<BuildResults>(
        API_CONFIG.ENDPOINTS.EXECUTIONS.BUILD_RESULTS(buildId.toString())
      )
      return data
    },
    enabled: !!buildId,
  })
}

export const useTestPlanResults = (testplanId: number) => {
  return useQuery({
    queryKey: executionKeys.testplanResults(testplanId),
    queryFn: async () => {
      const { data } = await apiClient.get<TestPlanResults>(
        API_CONFIG.ENDPOINTS.EXECUTIONS.TESTPLAN_RESULTS(testplanId.toString())
      )
      return data
    },
    enabled: !!testplanId,
  })
}

// ============================================================================
// Test Runs (Aggregated View)
// OpenAPI: GET/POST /api/testruns, GET/PUT/DELETE /api/testruns/{id}
//          POST /api/testruns/{id}/start, POST /api/testruns/{id}/complete
//          POST /api/testruns/{id}/cancel, GET /api/testruns/{id}/progress
//          GET /api/testruns/{id}/executions
// ============================================================================

export const useTestRuns = (params?: {
  testplan_id?: number
  build_id?: number
  status?: string
  started_by?: number
  limit?: number
  offset?: number
}) => {
  return useQuery({
    queryKey: ['test-runs', 'list', params],
    queryFn: async () => {
      const qp = new URLSearchParams()
      if (params?.testplan_id)
        qp.append('testplan_id', params.testplan_id.toString())
      if (params?.build_id)
        qp.append('build_id', params.build_id.toString())
      if (params?.status) qp.append('status', params.status)
      if (params?.started_by)
        qp.append('started_by', params.started_by.toString())
      if (params?.limit) qp.append('limit', params.limit.toString())
      if (params?.offset) qp.append('offset', params.offset.toString())

      const url = qp.toString()
        ? `${API_CONFIG.ENDPOINTS.TEST_RUNS.LIST}?${qp.toString()}`
        : API_CONFIG.ENDPOINTS.TEST_RUNS.LIST
      const { data } = await apiClient.get<TestRun[] | { test_runs: TestRun[] }>(url)
      return Array.isArray(data) ? data : (data as { test_runs: TestRun[] }).test_runs || []
    },
  })
}

export const useTestRun = (id: number) => {
  return useQuery({
    queryKey: ['test-runs', 'detail', id],
    queryFn: async () => {
      const { data } = await apiClient.get<TestRun>(
        API_CONFIG.ENDPOINTS.TEST_RUNS.GET(id.toString())
      )
      return data
    },
    enabled: !!id,
  })
}

export const useTestRunExecutions = (testRunId: number) => {
  return useQuery({
    queryKey: ['test-runs', testRunId, 'executions'],
    queryFn: async () => {
      const { data } = await apiClient.get<Execution[]>(
        API_CONFIG.ENDPOINTS.TEST_RUNS.EXECUTIONS(testRunId.toString())
      )
      return data
    },
    enabled: !!testRunId,
  })
}

export const useTestRunProgress = (testRunId: number, enabled = true) => {
  return useQuery({
    queryKey: ['test-runs', testRunId, 'progress'],
    queryFn: async () => {
      const { data } = await apiClient.get(
        API_CONFIG.ENDPOINTS.TEST_RUNS.PROGRESS(testRunId.toString())
      )
      return data
    },
    enabled: !!testRunId && enabled,
    refetchInterval: enabled ? 5000 : false,
  })
}

export const useCreateTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (runData: {
      testplan_id: number
      build_id: number
      name: string
      started_by?: number
      notes?: string
    }) => {
      const { data } = await apiClient.post<TestRun>(
        API_CONFIG.ENDPOINTS.TEST_RUNS.CREATE,
        runData
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-runs'] })
    },
  })
}

export const useUpdateTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: { name?: string; notes?: string }
    }) => {
      const response = await apiClient.put<TestRun>(
        API_CONFIG.ENDPOINTS.TEST_RUNS.UPDATE(id.toString()),
        data
      )
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['test-runs'] })
      queryClient.invalidateQueries({
        queryKey: ['test-runs', 'detail', variables.id],
      })
    },
  })
}

export const useDeleteTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(
        API_CONFIG.ENDPOINTS.TEST_RUNS.DELETE(id.toString())
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-runs'] })
    },
  })
}

// POST /api/testruns/{id}/start
export const useStartTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await apiClient.post<TestRun>(
        API_CONFIG.ENDPOINTS.TEST_RUNS.START(id.toString())
      )
      return data
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['test-runs'] })
      queryClient.invalidateQueries({ queryKey: ['test-runs', 'detail', id] })
    },
  })
}

// POST /api/testruns/{id}/complete
export const useCompleteTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await apiClient.post<TestRun>(
        API_CONFIG.ENDPOINTS.TEST_RUNS.COMPLETE(id.toString())
      )
      return data
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['test-runs'] })
      queryClient.invalidateQueries({ queryKey: ['test-runs', 'detail', id] })
    },
  })
}

// POST /api/testruns/{id}/cancel
export const useCancelTestRun = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await apiClient.post<TestRun>(
        API_CONFIG.ENDPOINTS.TEST_RUNS.CANCEL(id.toString())
      )
      return data
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['test-runs'] })
      queryClient.invalidateQueries({ queryKey: ['test-runs', 'detail', id] })
    },
  })
}
